import React, { FC, FunctionComponent, SVGProps, useCallback, useEffect, useMemo, useState } from 'react';
import { moneyMaskNumber } from 'utils/mask/money';
import { Close } from '@material-ui/icons';
import { Icon } from '@material-ui/core';

import { useCatalog } from 'context/catalog/CatalogContext';
import { useCart } from 'context/cart/CartContext';
import styles from './ProductDetail.module.scss';
import useProductDetail from './UseProductDetail';
import ProductCompositions from 'components/productComposition/ProductCompositions';
import { useI18n } from 'utils/i18n/hooks/useI18n';
import { ReactComponent as Organic } from "assets/icons/organic-icon.svg";
import { ReactComponent as SugarFree } from "assets/icons/sugar-free-icon.svg";
import { ReactComponent as GlutenFree } from "assets/icons/gluten-free-icon.svg";
import { ReactComponent as LactoseFree } from "assets/icons/lactose-free-icon.svg";
import { ReactComponent as Vegan } from "assets/icons/vegan-icon.svg";
import { ReactComponent as Vegetarian } from "assets/icons/vegetarian-icon.svg";

const ID_ONE_PRODUCT_PER_TIME = 'e703e514-652d-0230-7593-5697f769bcd1';

export interface IProductProps {
    productId: string;
}

const ProductDetail: FC<IProductProps> = ({ productId }) => {
    const {
        product,
        compositions,
        quantity,
        onClickAddQuantityHandle,
        onClickRemoveQuantityHandle,
        onClickAddProduct,
        onChangeCompositionHandle,
        onClickBack,
        handleChangeObservation
    } = useProductDetail({ productId });

    const { cartItems } = useCart();
    const { catalog } = useCatalog()
    const { t, currentLanguage } = useI18n();
    
    const [hasImageError, setHasImageError] = useState(false);
    const [opacityHeader, setOpacityHeader] = useState(1);
    const [disabledAddProduct, setDisabledAddProduct] = useState(true);
    
    const productNameLocalized = (product?.translations?.find(translation => translation.lang === currentLanguage)?.name ?? product?.name) ?? "";
    const productDescriptionLocalized = (product?.translations?.find(translation => translation.lang === currentLanguage)?.productInformation ?? product?.description) ?? "";

    const hidePayButton = useMemo(() => {
        //Garden Cidade Nova
        return sessionStorage.getItem("@listid")?.toString() === "b798440e-bc00-4d82-a978-7ab7a0fe4cd6";
    }, []);

    const foodCategoryIcons: Record<string, FunctionComponent<SVGProps<SVGSVGElement> & { title?: string | undefined; }>> = {
        "Orgânico": Organic,
        "Sem açúcar": SugarFree,
        "Sem glúten": GlutenFree,
        "Sem lactose": LactoseFree,
        Vegano: Vegan,
        Vegetariano: Vegetarian,
    };

    const categoriesWithIcons = product?.foodCategories?.map((category) => ({
        ...category,
        icon: foodCategoryIcons[category.name] ?? undefined,
    }));

    useEffect(() => {
        return () => {
            window.scrollTo({ top: document.documentElement.scrollTop - 1 });
        }
    }, [])

    const onScroll = useCallback(
        () => {
            const opacity = (200 - window.pageYOffset) / 200;
            if (opacity > 0) {
                setOpacityHeader(opacity);
            } else {
                setOpacityHeader(0);
            }
        },
        [],
    )

    useEffect(() => {
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => {
            window.removeEventListener("scroll", onScroll)
        }
    }, [onScroll]);

    useEffect(() => {
        if (!!compositions?.unvalidatedCompositions.length) {
            setDisabledAddProduct(true)
        } else {
            setDisabledAddProduct(false)
        }
        return () => {

        }
    }, [compositions])

    return (
        product ?
            <div id={styles.ProductDetail} >
                <div className={styles.container}>
                    <button type='button' className={styles.backButton} onClick={onClickBack}>
                        <Close className={styles.backButtonIcon} />
                    </button>

                    {
                        !hasImageError ?
                            <img
                                src={product.thumbnailUrl.replace(/(\.jpg|\.jpeg|\.png)(\?.*)?$/, '_large$1$2')}
                                alt=""
                                onError={(e) => {
                                    if (e.currentTarget.src.indexOf('_large') !== -1) {
                                        e.currentTarget.src = product.thumbnailUrl;
                                        return;
                                    }
                                    setHasImageError(true)
                                }}
                                className={styles.imageContainer}
                            /> :
                            <div className={styles.imageFallback}>
                                <Icon className={styles.icon}>no_photography</Icon>
                            </div>
                    }

                    <div className={styles.content}>
                        <div className={styles.name} >
                            {productNameLocalized}
                        </div>

                        <div className={styles.description}>
                            {productDescriptionLocalized}
                        </div>

                        {categoriesWithIcons?.length ? (
                            <div className={styles.foodCategories}>
                                {categoriesWithIcons?.map((category) => (
                                    <category.icon key={category.id} className={styles.icon}  style={{ width: "16px", height: "16px" }}/>
                                ))}
                            </div>
                        ) : null}

                        <div className={styles.mobilePrice}>
                            {moneyMaskNumber(product?.price)}
                        </div>
                    </div>

                    {product.compositions && !catalog?.hideCompositions &&
                        <div className={styles.productComposition}>
                            <ProductCompositions
                                productCompositions={product.compositions}
                                onChangeOrderCompositions={onChangeCompositionHandle}
                                disabled={!catalog?.enableShoppingCart}
                            />

                            <div>
                                {
                                    !!product.productGeneralObservation &&
                                    <>
                                        <label className={styles.obsLabel}>{t("catalog.products.observationLabel")}</label>
                                        <textarea
                                            placeholder={t("catalog.products.observationPlaceholder")}
                                            rows={5}
                                            max-length={200}
                                            onChange={handleChangeObservation}
                                            className={styles.obsInput}
                                        />
                                    </>
                                }
                            </div>
                        </div>
                    }
                    {
                        (!(catalog?.enableShoppingCart === false) && !hidePayButton) &&
                        <footer>
                            {ID_ONE_PRODUCT_PER_TIME !== catalog?.id ? (
                                <div className={styles.quantityContainer}>
                                    <button
                                        type="button"
                                        className={styles.iconButton}
                                        disabled={!(quantity > 1)}
                                        onClick={onClickRemoveQuantityHandle}
                                    >
                                        <Icon>remove</Icon>
                                    </button>
                                    <span className={styles.quantityText}>
                                        {quantity}
                                    </span>
                                    <button
                                        type="button"
                                        className={styles.iconButton}
                                        onClick={onClickAddQuantityHandle}
                                    >
                                        <Icon>add</Icon>
                                    </button>
                                </div>
                            ) : <div></div>}
                            <button className={styles.submitButton} disabled={disabledAddProduct || (cartItems.length > 0 && ID_ONE_PRODUCT_PER_TIME === catalog?.id)} onClick={onClickAddProduct}>
                                <p>{t("catalog.products.addToCart")}</p>
                                <span> {moneyMaskNumber(quantity * (product.price + (compositions?.totals.price ?? 0)))}</span>

                            </button>
                        </footer>
                    }
                </div>
            </div>
            :
            <div>
                {t("catalog.products.loadingProduct")}
            </div>
    )
}
export default ProductDetail
